import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { noteFailure } from './failures';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** True when the build carries a database URL + public key. A clean clone falls back to .env.demo. */
export const isConfigured = Boolean(url && anonKey);

/** Read before the client below consumes the fragment: did this page load from a password-reset link? (§10 A2) */
export const landedWithRecovery = typeof window !== 'undefined' && /(^|[#&])type=recovery(&|$)/.test(window.location.hash);

/** The one browser client. Bounded by RLS; holds the anon key, never a service credential. */
export const supabase: SupabaseClient = createClient(url || 'http://invalid.local', anonKey || 'missing', {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: 'rmp2.auth' },
});

/** What a public page says when a call of its own fails. */
export const PUBLIC_FAILED = 'Something went wrong. Please try again in a moment.';

/**
 * The public door's transport: a plain PostgREST RPC call carrying ONLY the anon key. No auth client, so it
 * never reads or attaches an operator's session — a registration link behaves identically whoever is signed
 * in elsewhere in the same browser (§10 c10-25), and public writes are never attributed to an operator.
 */
export async function publicRpc<T>(fn: string, args: Record<string, unknown>): Promise<{ data: T | null; error: { message: string; status: number } | null }> {
  try {
    const r = await fetch(`${url}/rest/v1/rpc/${fn}`, {
      method: 'POST',
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    });
    const text = await r.text();
    if (!r.ok) {
      // noted under the sentence the public pages show; only a server failure is one a guest can report
      noteFailure({ reason: r.status >= 500 ? 'server' : 'rejected', message: PUBLIC_FAILED, apiPath: `/rest/v1/rpc/${fn}`, apiStatus: r.status });
      return { data: null, error: { message: text.slice(0, 200), status: r.status } };
    }
    return { data: (text ? JSON.parse(text) : null) as T | null, error: null };
  } catch (e) {
    noteFailure({ reason: 'network', message: PUBLIC_FAILED, apiPath: `/rest/v1/rpc/${fn}` });
    return { data: null, error: { message: (e as Error).message, status: 0 } };
  }
}
